import { Injectable, computed, signal } from '@angular/core';
import type { Coordinates } from '../domain/geo';

export type LocationStatus =
  /** Aucune demande faite. C'est l'état au chargement, toujours. */
  | 'idle'
  | 'locating'
  | 'located'
  /** L'utilisateur a refusé, ou le navigateur a déjà mémorisé un refus. */
  | 'denied'
  /** Position indisponible ou délai dépassé : réessayer a du sens. */
  | 'failed'
  /** Pas d'API de géolocalisation, ou contexte non sécurisé. */
  | 'unsupported';

/**
 * Position du client, obtenue du navigateur.
 *
 * Règle qui gouverne ce service : **la localisation ne part jamais sans un
 * geste de l'utilisateur**. `locate()` n'est appelée que depuis un gestionnaire
 * de clic, jamais au démarrage d'un écran ni dans un effet.
 *
 * Cela vaut même quand le navigateur a déjà mémorisé l'autorisation. Interroger
 * `navigator.permissions` pour se localiser sans invite serait techniquement
 * possible et silencieux — donc exactement ce qu'on refuse : une autorisation
 * accordée hier n'est pas une demande formulée aujourd'hui.
 *
 * La position n'est ni écrite sur le disque ni envoyée à l'API. Elle vit dans
 * l'onglet et disparaît avec lui, ce qui évite d'avoir à justifier une
 * conservation de donnée de localisation.
 */
@Injectable({ providedIn: 'root' })
export class GeolocationService {
  private readonly state = signal<LocationStatus>(
    typeof navigator !== 'undefined' && 'geolocation' in navigator ? 'idle' : 'unsupported',
  );
  private readonly coordinates = signal<Coordinates | undefined>(undefined);

  readonly status = this.state.asReadonly();
  readonly position = this.coordinates.asReadonly();
  readonly isLocated = computed(() => this.coordinates() !== undefined);

  /**
   * Demande la position. À n'appeler que depuis une action de l'utilisateur.
   *
   * Précision grossière volontairement : savoir quels producteurs sont proches
   * ne demande pas le mètre près. On épargne la batterie, et on collecte moins
   * précis là où le précis ne sert à rien. La position en cache est acceptée
   * jusqu'à cinq minutes, pour ne pas relancer le capteur à chaque retour sur
   * l'écran.
   */
  locate(): void {
    if (this.state() === 'unsupported' || this.state() === 'locating') {
      return;
    }
    void this.requestPosition().catch(() => undefined);
  }

  /**
   * Même demande que `locate()`, mais sous forme de promesse.
   *
   * Utile quand l'appelant doit enchaîner — reverse géocoding pour préremplir
   * une adresse — sans observer les signaux. Le refus ou l'échec rejettent.
   */
  requestPosition(): Promise<Coordinates> {
    if (this.state() === 'unsupported') {
      return Promise.reject(new Error('unsupported'));
    }
    if (this.state() === 'locating') {
      return Promise.reject(new Error('locating'));
    }

    this.state.set('locating');

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          this.coordinates.set(coordinates);
          this.state.set('located');
          resolve(coordinates);
        },
        (error) => {
          this.coordinates.set(undefined);
          this.state.set(error.code === error.PERMISSION_DENIED ? 'denied' : 'failed');
          reject(error);
        },
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
      );
    });
  }

  /**
   * Oublie la position.
   *
   * Le symétrique de `locate()` : on peut retirer ce qu'on a donné. L'écran
   * revient au classement sans distance, sans que l'utilisateur ait à fouiller
   * les réglages du navigateur.
   */
  forget(): void {
    this.coordinates.set(undefined);
    this.state.set(this.state() === 'unsupported' ? 'unsupported' : 'idle');
  }
}
